/* ==========================================================================
   Admin Provider Management Page
   Task F24: Provider management for pharmacy admins
   ========================================================================== */

import { useState, useEffect, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Search,
  UserPlus,
  Settings,
  Trash2,
  MoreVertical,
  RefreshCw,
  Mail,
  UserX,
  UserCheck,
  Package,
  MapPin,
  Eye,
} from 'lucide-react'
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Alert } from '@/components/ui/Alert'
import { Select } from '@/components/ui/Select'
import { InviteProviderModal } from '@/components/admin/InviteProviderModal'
import { CatalogAssignmentModal } from '@/components/admin/CatalogAssignmentModal'
import { ProviderSettingsModal } from '@/components/admin/ProviderSettingsModal'
import { ProviderAddressesModal } from '@/components/admin/ProviderAddressesModal'
import { ProviderStatusBadge } from '@/components/admin/ProviderStatusBadge'
import {
  DeleteProviderModal,
  DisableProviderModal,
} from '@/components/admin/ProviderConfirmModals'
import { AdminTableSkeleton } from '@/components/skeletons'
import { useAdminAuth } from '@/hooks/useAdminAuth'
import { useToast } from '@/context/ToastContext'
import {
  listProviders,
  listCatalog,
  inviteProvider,
  updateProvider,
  deleteProvider,
  resendProviderInvite,
  getProviderCatalogAssignments,
  updateProviderCatalogAssignments,
  updatePharmacy,
} from '@/api/admin'
import { ApiClientError } from '@/api/client'
import { ProviderStatus } from '@/api/types'
import type {
  ProviderResponse,
  ProviderInvite,
  ProviderUpdate,
  CatalogProductResponse,
} from '@/api/types'

/* --------------------------------------------------------------------------
   Constants
   -------------------------------------------------------------------------- */

const ITEMS_PER_PAGE = 10

/* --------------------------------------------------------------------------
   Pagination Component
   -------------------------------------------------------------------------- */

interface PaginationProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
}

function Pagination({ currentPage, totalPages, onPageChange }: PaginationProps) {
  if (totalPages <= 1) return null

  const getPageNumbers = () => {
    const pages: (number | 'ellipsis')[] = []
    const showPages = 5

    if (totalPages <= showPages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i)
        pages.push('ellipsis')
        pages.push(totalPages)
      } else if (currentPage >= totalPages - 2) {
        pages.push(1)
        pages.push('ellipsis')
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i)
      } else {
        pages.push(1)
        pages.push('ellipsis')
        pages.push(currentPage - 1)
        pages.push(currentPage)
        pages.push(currentPage + 1)
        pages.push('ellipsis')
        pages.push(totalPages)
      }
    }

    return pages
  }

  return (
    <div className="flex items-center justify-center gap-1 sm:gap-2">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="px-3 py-1.5 text-sm text-charcoal hover:bg-gray-100 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        aria-label="Previous page"
      >
        ←
      </button>

      {getPageNumbers().map((page, index) =>
        page === 'ellipsis' ? (
          <span key={`ellipsis-${index}`} className="px-2 py-1 text-gray-500">
            ...
          </span>
        ) : (
          <button
            key={page}
            onClick={() => onPageChange(page)}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
              page === currentPage
                ? 'bg-deep-purple text-white'
                : 'text-charcoal hover:bg-gray-100'
            }`}
            aria-label={`Page ${page}`}
            aria-current={page === currentPage ? 'page' : undefined}
          >
            {page}
          </button>
        )
      )}

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="px-3 py-1.5 text-sm text-charcoal hover:bg-gray-100 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        aria-label="Next page"
      >
        →
      </button>
    </div>
  )
}

/* --------------------------------------------------------------------------
   Empty State Component
   -------------------------------------------------------------------------- */

function EmptyState({ onInviteProvider }: { onInviteProvider: () => void }) {
  return (
    <div className="text-center py-12">
      <div className="mx-auto w-16 h-16 bg-soft-lavender rounded-full flex items-center justify-center mb-4">
        <UserPlus className="w-8 h-8 text-deep-purple" />
      </div>
      <h3 className="text-lg font-semibold text-charcoal mb-2">
        No providers yet
      </h3>
      <p className="text-gray-600 mb-6 max-w-sm mx-auto">
        Add your first provider to start accepting prescriptions. You can send
        an email invitation or create their account directly with a password.
      </p>
      <Button variant="primary" onClick={onInviteProvider}>
        <UserPlus className="w-4 h-4 mr-2" />
        Add Your First Provider
      </Button>
    </div>
  )
}


/* --------------------------------------------------------------------------
   Format Date Helper
   -------------------------------------------------------------------------- */

function formatDateTime(dateString: string | null): string {
  if (!dateString) return 'Never'
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

/* --------------------------------------------------------------------------
   Status Filter Options
   -------------------------------------------------------------------------- */

const statusFilterOptions = [
  { value: 'all', label: 'All Statuses' },
  { value: ProviderStatus.ACTIVE, label: 'Active' },
  { value: ProviderStatus.INVITED, label: 'Invited' },
  { value: ProviderStatus.DISABLED, label: 'Disabled' },
]

/* --------------------------------------------------------------------------
   Main Component
   -------------------------------------------------------------------------- */

export function Providers() {
  const { pharmacy, refreshPharmacy } = useAdminAuth()
  const { success: showSuccess, error: showError } = useToast()
  const navigate = useNavigate()
  const slug = pharmacy?.portal_slug || ''
  // Canadian pharmacies have no NPI / DEA (PORTAL-161)
  const isCanadianPharmacy = pharmacy?.country === 'CA'

  // Navigate to a provider's details page (PORTAL-74).
  function goToProviderDetails(provider: ProviderResponse) {
    navigate(`/rx/${slug}/admin/providers/${provider.id}`)
  }

  // Navigate to a provider's per-provider settings page (PORTAL-72).
  function goToProviderSettings(provider: ProviderResponse) {
    navigate(`/rx/${slug}/admin/providers/${provider.id}/settings`)
  }

  // Data state
  const [providers, setProviders] = useState<ProviderResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)

  // Modal state
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Delete confirmation state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [providerToDelete, setProviderToDelete] = useState<ProviderResponse | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  // Disable confirmation state
  const [disableConfirmOpen, setDisableConfirmOpen] = useState(false)
  const [providerToDisable, setProviderToDisable] = useState<ProviderResponse | null>(null)
  const [isToggling, setIsToggling] = useState(false)

  // Catalog assignment state
  const [catalogModalOpen, setCatalogModalOpen] = useState(false)
  const [catalogProvider, setCatalogProvider] = useState<ProviderResponse | null>(null)
  const [catalogProducts, setCatalogProducts] = useState<CatalogProductResponse[]>([])
  const [assignedProductIds, setAssignedProductIds] = useState<string[]>([])
  const [isSavingCatalog, setIsSavingCatalog] = useState(false)

  // Provider settings modal state
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false)
  const [isSavingSettings, setIsSavingSettings] = useState(false)

  // Per-provider practice addresses modal state (defaults are managed inline
  // within the address editor — issue #192)
  const [addressesModalOpen, setAddressesModalOpen] = useState(false)
  const [addressesProvider, setAddressesProvider] = useState<ProviderResponse | null>(null)
  // Effective (per-provider) country-field setting for the open addresses modal
  // (PORTAL-72). Falls back to the pharmacy global until resolved.
  const [addressesCountryField, setAddressesCountryField] = useState(false)

  // Fetch providers
  const fetchProviders = useCallback(async () => {
    if (!slug) return

    setLoading(true)
    setError(null)

    try {
      const response = await listProviders(slug)
      setProviders(response.providers)
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? err.message
          : 'Failed to load providers. Please try again.'
      setError(message)
    } finally {
      setLoading(false)
    }
  }, [slug])

  useEffect(() => {
    fetchProviders()
  }, [fetchProviders])

  // Filter and search providers
  const filteredProviders = useMemo(() => {
    let result = providers

    // Filter by status
    if (statusFilter !== 'all') {
      result = result.filter((p) => p.status === statusFilter)
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      result = result.filter(
        (p) =>
          (p.name && p.name.toLowerCase().includes(query)) ||
          p.email.toLowerCase().includes(query) ||
          (p.npi && p.npi.includes(query)) ||
          (p.primary_clinic_name &&
            p.primary_clinic_name.toLowerCase().includes(query))
      )
    }

    return result
  }, [providers, searchQuery, statusFilter])

  // Pagination
  const totalPages = Math.ceil(filteredProviders.length / ITEMS_PER_PAGE)
  const paginatedProviders = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE
    return filteredProviders.slice(start, start + ITEMS_PER_PAGE)
  }, [filteredProviders, currentPage])

  // Reset to page 1 when search/filter changes
  useEffect(() => {
    setCurrentPage(1)
  }, [searchQuery, statusFilter])

  // Open invite modal
  function handleInviteProvider() {
    setIsInviteModalOpen(true)
  }

  // Close invite modal
  function handleCloseInviteModal() {
    setIsInviteModalOpen(false)
  }

  // Handle invite submission
  async function handleInviteSubmit(data: ProviderInvite) {
    setIsSubmitting(true)

    try {
      await inviteProvider(slug, data)
      showSuccess(
        data.password
          ? 'Provider account created successfully'
          : 'Invitation sent successfully'
      )
      handleCloseInviteModal()
      await fetchProviders()
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? err.message
          : data.password
            ? 'Failed to create provider account. Please try again.'
            : 'Failed to send invitation. Please try again.'
      showError(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  // Resend invitation
  async function handleResendInvite(provider: ProviderResponse) {
    try {
      await resendProviderInvite(slug, provider.id)
      showSuccess('Invitation resent successfully')
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? err.message
          : 'Failed to resend invitation. Please try again.'
      showError(message)
    }
  }

  // Open disable confirmation
  function handleDisableClick(provider: ProviderResponse) {
    setProviderToDisable(provider)
    setDisableConfirmOpen(true)
  }

  // Toggle provider status (enable/disable)
  async function handleToggleStatus() {
    if (!providerToDisable) return

    setIsToggling(true)

    try {
      const newStatus =
        providerToDisable.status === ProviderStatus.DISABLED
          ? ProviderStatus.ACTIVE
          : ProviderStatus.DISABLED

      const updateData: ProviderUpdate = { status: newStatus }
      await updateProvider(slug, providerToDisable.id, updateData)

      showSuccess(
        newStatus === ProviderStatus.DISABLED
          ? 'Provider disabled successfully'
          : 'Provider enabled successfully'
      )

      setDisableConfirmOpen(false)
      setProviderToDisable(null)
      await fetchProviders()
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? err.message
          : 'Failed to update provider status. Please try again.'
      showError(message)
    } finally {
      setIsToggling(false)
    }
  }

  // Enable provider directly (no confirmation needed)
  async function handleEnableProvider(provider: ProviderResponse) {
    try {
      const updateData: ProviderUpdate = { status: ProviderStatus.ACTIVE }
      await updateProvider(slug, provider.id, updateData)
      showSuccess('Provider enabled successfully')
      await fetchProviders()
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? err.message
          : 'Failed to enable provider. Please try again.'
      showError(message)
    }
  }

  // Open delete confirmation
  function handleDeleteClick(provider: ProviderResponse) {
    setProviderToDelete(provider)
    setDeleteConfirmOpen(true)
  }

  // Confirm delete
  async function handleConfirmDelete() {
    if (!providerToDelete) return

    setIsDeleting(true)

    try {
      await deleteProvider(slug, providerToDelete.id)
      showSuccess('Provider deleted successfully')
      setDeleteConfirmOpen(false)
      setProviderToDelete(null)
      await fetchProviders()
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? err.message
          : 'Failed to delete provider. Please try again.'
      showError(message)
    } finally {
      setIsDeleting(false)
    }
  }

  // Open catalog assignment modal
  async function handleManageCatalog(provider: ProviderResponse) {
    setCatalogProvider(provider)
    setCatalogModalOpen(true)

    try {
      // Fetch products and current assignments in parallel
      const [catalogResponse, assignmentsResponse] = await Promise.all([
        listCatalog(slug),
        getProviderCatalogAssignments(slug, provider.id),
      ])
      setCatalogProducts(catalogResponse.products)
      setAssignedProductIds(assignmentsResponse.product_ids)
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? err.message
          : 'Failed to load catalog data. Please try again.'
      showError(message)
      setCatalogModalOpen(false)
    }
  }

  // Save catalog assignments
  async function handleSaveCatalogAssignments(productIds: string[]) {
    if (!catalogProvider) return

    setIsSavingCatalog(true)

    try {
      await updateProviderCatalogAssignments(slug, catalogProvider.id, {
        product_ids: productIds,
      })
      showSuccess(
        productIds.length > 0
          ? `Custom catalog saved with ${productIds.length} product${productIds.length === 1 ? '' : 's'}`
          : 'Provider restored to full catalog'
      )
      setCatalogModalOpen(false)
      setCatalogProvider(null)
      await fetchProviders()
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? err.message
          : 'Failed to save catalog assignments. Please try again.'
      showError(message)
    } finally {
      setIsSavingCatalog(false)
    }
  }

  // Open per-provider practice addresses modal (issues #140 / #180 / #192).
  // Country-field visibility follows the provider's effective setting
  // (PORTAL-72), which the provider list already carries — no extra fetch. Fall
  // back to the pharmacy global if the field is somehow absent.
  function handleManageAddresses(provider: ProviderResponse) {
    setAddressesProvider(provider)
    setAddressesCountryField(
      provider.effective_allow_country_field ?? pharmacy?.allow_country_field ?? false
    )
    setAddressesModalOpen(true)
  }

  async function handleSaveProviderSettings(data: { npi_required: boolean; dea_required: boolean }) {
    if (!slug) return
    setIsSavingSettings(true)
    try {
      await updatePharmacy(slug, data)
      await refreshPharmacy()
      setIsSettingsModalOpen(false)
      showSuccess('Provider settings updated')
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? err.getUserMessage()
          : 'Failed to save provider settings. Please try again.'
      showError(message)
    } finally {
      setIsSavingSettings(false)
    }
  }

  return (
    <div className="p-6 sm:p-8">
      <div className="max-w-6xl">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="font-serif text-2xl sm:text-3xl text-charcoal">Providers</h1>
            <p className="text-charcoal/70 mt-1">
              Manage providers who can submit prescriptions
            </p>
          </div>
          <div className="flex gap-3">
            {!isCanadianPharmacy && (
              <Button variant="secondary" onClick={() => setIsSettingsModalOpen(true)}>
                <Settings className="w-4 h-4 mr-2" />
                Provider Settings
              </Button>
            )}
            <Button variant="primary" onClick={handleInviteProvider}>
              <UserPlus className="w-4 h-4 mr-2" />
              Add Provider
            </Button>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="error" className="mb-6">
            {error}
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchProviders}
              className="ml-4"
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              Retry
            </Button>
          </Alert>
        )}

        {/* Main Content */}
        <Card className="overflow-visible">
          {loading ? (
            <AdminTableSkeleton columns={7} rows={5} />
          ) : providers.length === 0 ? (
            <EmptyState onInviteProvider={handleInviteProvider} />
          ) : (
            <>
              {/* Search and Filters */}
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                {/* Search Input */}
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="text"
                    placeholder={
                      isCanadianPharmacy
                        ? 'Search by name, email, or clinic...'
                        : 'Search by name, email, NPI, or clinic...'
                    }
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>

                {/* Status Filter */}
                <div className="w-full sm:w-48">
                  <Select
                    value={statusFilter}
                    onChange={(value) => setStatusFilter(value)}
                    options={statusFilterOptions}
                  />
                </div>
              </div>

              {/* Results Summary */}
              <div className="text-sm text-gray-600 mb-4">
                Showing {paginatedProviders.length} of {filteredProviders.length} providers
                {searchQuery && ` matching "${searchQuery}"`}
              </div>

              {/* Providers Table */}
              {filteredProviders.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No providers found matching your criteria.
                </div>
              ) : (
                <>
                  {/* Desktop Table */}
                  <div className="hidden sm:block">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left text-sm font-medium text-gray-600 pb-3">
                            Provider
                          </th>
                          <th className="text-left text-sm font-medium text-gray-600 pb-3">
                            Email
                          </th>
                          {!isCanadianPharmacy && (
                            <th className="text-left text-sm font-medium text-gray-600 pb-3">
                              NPI
                            </th>
                          )}
                          <th className="text-left text-sm font-medium text-gray-600 pb-3">
                            Clinic
                          </th>
                          <th className="text-left text-sm font-medium text-gray-600 pb-3">
                            Status
                          </th>
                          <th className="text-left text-sm font-medium text-gray-600 pb-3">
                            Last Login
                          </th>
                          <th className="text-right text-sm font-medium text-gray-600 pb-3">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedProviders.map((provider) => (
                          <tr
                            key={provider.id}
                            onClick={(e) => {
                              // Mouse convenience only — the keyboard/AT path is the
                              // "View Details" action in the row menu. Ignore
                              // clicks originating inside the actions menu.
                              if ((e.target as HTMLElement).closest('[data-row-actions]')) return
                              goToProviderDetails(provider)
                            }}
                            className="cursor-pointer border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors"
                          >
                            <td className="py-4 pr-4">
                              <div className="text-charcoal font-medium">
                                {provider.name || '—'}
                              </div>
                              {provider.license_state && provider.license_number && (
                                <div className="text-sm text-gray-500 mt-0.5">
                                  {provider.license_state} Lic: {provider.license_number}
                                </div>
                              )}
                            </td>
                            <td className="py-4 pr-4 text-gray-600">
                              {provider.email}
                            </td>
                            {!isCanadianPharmacy && (
                              <td className="py-4 pr-4">
                                {provider.npi ? (
                                  <code className="text-sm bg-gray-100 px-2 py-0.5 rounded">
                                    {provider.npi}
                                  </code>
                                ) : (
                                  <span className="text-gray-400">—</span>
                                )}
                              </td>
                            )}
                            <td className="py-4 pr-4">
                              {provider.address_count === 0 ? (
                                <span className="text-gray-400">No clinic</span>
                              ) : (
                                <>
                                  <div className="text-charcoal">
                                    {provider.primary_clinic_name || 'Practice Address'}
                                  </div>
                                  {provider.address_count > 1 && (
                                    <div className="text-sm text-gray-400 mt-0.5">
                                      +{provider.address_count - 1} more{' '}
                                      {provider.address_count - 1 === 1
                                        ? 'address'
                                        : 'addresses'}
                                    </div>
                                  )}
                                </>
                              )}
                            </td>
                            <td className="py-4 pr-4">
                              <div className="flex items-center gap-1.5">
                                <ProviderStatusBadge status={provider.status} />
                                {provider.has_custom_catalog && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                                    Custom
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="py-4 pr-4 text-gray-600 text-sm">
                              {formatDateTime(provider.last_login_at)}
                            </td>
                            <td className="py-4 text-right" data-row-actions>
                              <Menu as="div" className="relative inline-block text-left">
                                <MenuButton className="p-2 rounded-md hover:bg-gray-100 transition-colors">
                                  <MoreVertical className="w-4 h-4 text-gray-500" />
                                </MenuButton>
                                <MenuItems className="absolute right-0 z-50 mt-1 w-48 bg-white rounded-md shadow-lg ring-1 ring-black/5 focus:outline-none">
                                  <div className="py-1">
                                    {/* View Details (PORTAL-74) */}
                                    <MenuItem>
                                      {({ active }) => (
                                        <button
                                          onClick={() => goToProviderDetails(provider)}
                                          className={`flex items-center w-full px-4 py-2 text-sm ${
                                            active ? 'bg-gray-100' : ''
                                          } text-charcoal`}
                                        >
                                          <Eye className="w-4 h-4 mr-2" />
                                          View Details
                                        </button>
                                      )}
                                    </MenuItem>

                                    {/* Override Settings (PORTAL-72) */}
                                    <MenuItem>
                                      {({ active }) => (
                                        <button
                                          onClick={() => goToProviderSettings(provider)}
                                          className={`flex items-center w-full px-4 py-2 text-sm ${
                                            active ? 'bg-gray-100' : ''
                                          } text-charcoal`}
                                        >
                                          <Settings className="w-4 h-4 mr-2" />
                                          Override Settings
                                        </button>
                                      )}
                                    </MenuItem>

                                    {/* Manage Catalog */}
                                    <MenuItem>
                                      {({ active }) => (
                                        <button
                                          onClick={() => handleManageCatalog(provider)}
                                          className={`flex items-center w-full px-4 py-2 text-sm ${
                                            active ? 'bg-gray-100' : ''
                                          } text-charcoal`}
                                        >
                                          <Package className="w-4 h-4 mr-2" />
                                          Manage Catalog
                                        </button>
                                      )}
                                    </MenuItem>

                                    {/* Practice Addresses (issues #140 / #180 / #192 — payment
                                        / delivery defaults are configured per address inside
                                        this modal) */}
                                    <MenuItem>
                                      {({ active }) => (
                                        <button
                                          onClick={() => handleManageAddresses(provider)}
                                          className={`flex items-center w-full px-4 py-2 text-sm ${
                                            active ? 'bg-gray-100' : ''
                                          } text-charcoal`}
                                        >
                                          <MapPin className="w-4 h-4 mr-2" />
                                          Practice Addresses
                                        </button>
                                      )}
                                    </MenuItem>

                                    {/* Resend Invite - only for invited status */}
                                    {provider.status === ProviderStatus.INVITED && (
                                      <MenuItem>
                                        {({ active }) => (
                                          <button
                                            onClick={() => handleResendInvite(provider)}
                                            className={`flex items-center w-full px-4 py-2 text-sm ${
                                              active ? 'bg-gray-100' : ''
                                            } text-charcoal`}
                                          >
                                            <Mail className="w-4 h-4 mr-2" />
                                            Resend Invitation
                                          </button>
                                        )}
                                      </MenuItem>
                                    )}

                                    {/* Enable - only for disabled status */}
                                    {provider.status === ProviderStatus.DISABLED && (
                                      <MenuItem>
                                        {({ active }) => (
                                          <button
                                            onClick={() => handleEnableProvider(provider)}
                                            className={`flex items-center w-full px-4 py-2 text-sm ${
                                              active ? 'bg-gray-100' : ''
                                            } text-charcoal`}
                                          >
                                            <UserCheck className="w-4 h-4 mr-2" />
                                            Enable Provider
                                          </button>
                                        )}
                                      </MenuItem>
                                    )}

                                    {/* Disable - only for active status */}
                                    {provider.status === ProviderStatus.ACTIVE && (
                                      <MenuItem>
                                        {({ active }) => (
                                          <button
                                            onClick={() => handleDisableClick(provider)}
                                            className={`flex items-center w-full px-4 py-2 text-sm ${
                                              active ? 'bg-gray-100' : ''
                                            } text-charcoal`}
                                          >
                                            <UserX className="w-4 h-4 mr-2" />
                                            Disable Provider
                                          </button>
                                        )}
                                      </MenuItem>
                                    )}

                                    {/* Delete */}
                                    <MenuItem>
                                      {({ active }) => (
                                        <button
                                          onClick={() => handleDeleteClick(provider)}
                                          className={`flex items-center w-full px-4 py-2 text-sm ${
                                            active ? 'bg-red-50' : ''
                                          } text-error`}
                                        >
                                          <Trash2 className="w-4 h-4 mr-2" />
                                          Delete Provider
                                        </button>
                                      )}
                                    </MenuItem>
                                  </div>
                                </MenuItems>
                              </Menu>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Cards */}
                  <div className="sm:hidden space-y-4">
                    {paginatedProviders.map((provider) => (
                      <div
                        key={provider.id}
                        onClick={(e) => {
                          // Mouse/touch convenience; the "Details" button is the
                          // keyboard path. Ignore taps on the card's own buttons.
                          if ((e.target as HTMLElement).closest('[data-row-actions]')) return
                          goToProviderDetails(provider)
                        }}
                        className="cursor-pointer border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="font-medium text-charcoal">
                            {provider.name || 'Unnamed Provider'}
                          </div>
                          <div className="flex items-center gap-1.5">
                            <ProviderStatusBadge status={provider.status} />
                            {provider.has_custom_catalog && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                                Custom
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-sm text-gray-600 mb-2">
                          {provider.email}
                        </div>
                        <div className="flex flex-wrap gap-2 text-sm text-gray-600 mb-3">
                          {!isCanadianPharmacy && provider.npi && (
                            <span className="bg-gray-100 px-2 py-0.5 rounded">
                              NPI: {provider.npi}
                            </span>
                          )}
                          {provider.license_state && (
                            <span className="bg-gray-100 px-2 py-0.5 rounded">
                              {provider.license_state}
                            </span>
                          )}
                        </div>
                        <div className="text-sm mb-2">
                          {provider.address_count === 0 ? (
                            <span className="text-gray-400">No clinic</span>
                          ) : (
                            <>
                              <span className="text-charcoal">
                                {provider.primary_clinic_name || 'Practice Address'}
                              </span>
                              {provider.address_count > 1 && (
                                <span className="text-gray-400">
                                  {' '}
                                  +{provider.address_count - 1} more{' '}
                                  {provider.address_count - 1 === 1
                                    ? 'address'
                                    : 'addresses'}
                                </span>
                              )}
                            </>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 mb-3">
                          Last login: {formatDateTime(provider.last_login_at)}
                        </div>
                        <div className="flex flex-wrap gap-2" data-row-actions>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => goToProviderDetails(provider)}
                          >
                            <Eye className="w-3.5 h-3.5 mr-1" />
                            Details
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => goToProviderSettings(provider)}
                          >
                            <Settings className="w-3.5 h-3.5 mr-1" />
                            Override Settings
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleManageCatalog(provider)}
                          >
                            <Package className="w-3.5 h-3.5 mr-1" />
                            Catalog
                          </Button>
                          {provider.status === ProviderStatus.INVITED && (
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => handleResendInvite(provider)}
                            >
                              <Mail className="w-3.5 h-3.5 mr-1" />
                              Resend
                            </Button>
                          )}
                          {provider.status === ProviderStatus.DISABLED && (
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => handleEnableProvider(provider)}
                            >
                              <UserCheck className="w-3.5 h-3.5 mr-1" />
                              Enable
                            </Button>
                          )}
                          {provider.status === ProviderStatus.ACTIVE && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDisableClick(provider)}
                            >
                              <UserX className="w-3.5 h-3.5 mr-1" />
                              Disable
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteClick(provider)}
                            className="text-error hover:text-error"
                          >
                            <Trash2 className="w-3.5 h-3.5 mr-1" />
                            Delete
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="mt-6 pt-4 border-t border-gray-200">
                      <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                      />
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </Card>
      </div>

      {/* Invite Provider Modal */}
      <InviteProviderModal
        showNpi={!isCanadianPharmacy}
        open={isInviteModalOpen}
        onClose={handleCloseInviteModal}
        onSubmit={handleInviteSubmit}
        loading={isSubmitting}
      />

      {/* Disable Confirmation Modal */}
      <DisableProviderModal
        open={disableConfirmOpen}
        provider={providerToDisable}
        loading={isToggling}
        onCancel={() => setDisableConfirmOpen(false)}
        onConfirm={handleToggleStatus}
      />

      {/* Delete Confirmation Modal */}
      <DeleteProviderModal
        open={deleteConfirmOpen}
        provider={providerToDelete}
        loading={isDeleting}
        onCancel={() => setDeleteConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
      />

      {/* Catalog Assignment Modal */}
      <CatalogAssignmentModal
        open={catalogModalOpen}
        onClose={() => {
          setCatalogModalOpen(false)
          setCatalogProvider(null)
        }}
        onSave={handleSaveCatalogAssignments}
        provider={catalogProvider}
        products={catalogProducts}
        assignedProductIds={assignedProductIds}
        loading={isSavingCatalog}
      />

      <ProviderSettingsModal
        open={isSettingsModalOpen}
        onClose={() => setIsSettingsModalOpen(false)}
        pharmacy={pharmacy}
        onSave={handleSaveProviderSettings}
        loading={isSavingSettings}
      />

      {/* Per-Provider Practice Addresses Modal (issues #140 / #180 / #192 —
          payment/delivery defaults are configured per address here) */}
      <ProviderAddressesModal
        open={addressesModalOpen}
        onClose={() => {
          setAddressesModalOpen(false)
          setAddressesProvider(null)
        }}
        slug={slug}
        provider={addressesProvider}
        showCountryField={addressesCountryField}
        defaultCountry={pharmacy?.country}
      />
    </div>
  )
}

export default Providers
